import React, { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';

import { URLS } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';
import { logger } from '@/utils/logging';

export interface TradingCardsAPIClient {
    fetch(endpoint: string, options?: RequestInit): Promise<any>;
    setApiUrl(url: string): void;
    apiUrl: string;
}

const TradingCardsClientContext = createContext<TradingCardsAPIClient | undefined>(undefined);

export const useTradingCardsClient = () => {
    const context = useContext(TradingCardsClientContext);
    if (!context) {
        throw new Error('useTradingCardsClient must be used within a TradingCardsClientProvider');
    }
    return context;
};

export const ripsKeys = {
    all: ['rips-all'] as const,
    initiateSession: (userId: string) => [...ripsKeys.all, 'initiate-session', userId] as const,
    getGames: () => [...ripsKeys.all, 'get-games'] as const,
    recentPacks: () => [...ripsKeys.all, 'recent-packs'] as const,
    inventoryCount: () => [...ripsKeys.all, 'inventory-count'] as const,
};

// Resolved lazily: URLS is env-dependent and must not be read at module load (before setEnv()).
let apiUrlOverride: string | undefined;
const getActiveApiUrl = () => apiUrlOverride ?? URLS.TRADING_CARDS_API_URL;

export const tradingCardsFetch = async (endpoint: string, options?: RequestInit) => {
    const { actions, session, guest } = useUserStore.getState();
    if (actions.sessionHasExpired() || guest) {
        // if the user session has expired or he's a guest don't fetch anything
        return null;
    }

    const defaultHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`,
    };

    const response = await fetch(`${getActiveApiUrl()}${endpoint}`, {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options?.headers,
        },
    });

    if (!response.ok) {
        logger.warn('[TradingCardsAPI]', `HTTP error! status: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    // session delete returns a 204 with no body
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
    }

    return response.json();
};

export const TradingCardsClientProvider = ({ children }: PropsWithChildren) => {
    const [apiUrl, setApiUrl] = useState(getActiveApiUrl);
    const client: TradingCardsAPIClient = useMemo(() => {
        return {
            apiUrl,
            setApiUrl: url => {
                apiUrlOverride = url;
                setApiUrl(url);
            },
            fetch: tradingCardsFetch,
        };
    }, [apiUrl]);
    return <TradingCardsClientContext.Provider value={client}>{children}</TradingCardsClientContext.Provider>;
};
